import { withProps } from 'recompose';
import { get } from 'lodash';
import { toTitleCase } from 'utils/format.js';

export default withProps((props) => {
	const baseUrl = props.baseUrl || '';
	// Use canonicalWithQueryParams if available, otherwise fall back to canonicalUrl
	const canonicalUrl =
		props.canonicalWithQueryParams || props.canonicalUrl || '';

	// Derive city/state from props.searchContent.location or searchParams
	let city = get(props, 'searchParams.city');
	city = toTitleCase(city);
	const state = get(props, 'searchParams.state');
	const mapUrl = props.staticMapUrl;

	const pageSchema = {
		'@context': 'https://schema.org',
		'@graph': [
			{
				'@type': 'WebSite',
				'@id': 'https://www.sparefoot.com',
				url: 'https://www.sparefoot.com/',
				name: 'SpareFoot',
			},
			{
				'@type': 'WebPage',
				'@id': canonicalUrl,
				url: canonicalUrl,
				inLanguage: 'en',
				name: props.metaTitle,
				isPartOf: { '@id': 'https://www.sparefoot.com' },
				description: props.metaDescription,
			},
		],
	};

	const webPageSchemaV2 = {
		'@context': 'https://schema.org',
		'@type': 'WebPage',
		'@id': `${canonicalUrl}#webpage`,
		url: canonicalUrl,
		name: props.metaTitle,
		description: props.metaDescription,
		isPartOf: {
			'@id': `https://www.sparefoot.com/#website`,
		},
	};

	const pageSchemaV2 = {
		'@context': 'https://schema.org',
		'@type': 'WebSite',
		'@id': 'https://www.sparefoot.com/#website',
		name: 'SpareFoot',
		url: 'https://www.sparefoot.com/',
		sameAs: [
			'https://www.facebook.com/SpareFoot/',
			'https://www.instagram.com/sparefoot/',
			'https://x.com/SpareFoot',
			'https://www.youtube.com/@SpareFootSS',
			'https://en.wikipedia.org/wiki/SpareFoot',
		],
		potentialAction: {
			'@type': 'SearchAction',
			target: 'https://www.sparefoot.com/search?location={search_term_string}',
			'query-input': 'required name=search_term_string',
		},
	};

	const clpPageSchema = {
		'@context': 'https://schema.org',
		'@graph': [
			{
				'@type': 'WebSite',
				'@id': 'https://www.sparefoot.com',
				url: 'https://www.sparefoot.com/',
				name: 'SpareFoot',
				potentialAction: {
					'@type': 'SearchAction',
					target: {
						'@type': 'EntryPoint',
						urlTemplate: (() => {
							if (!city || !state)
								return 'https://www.sparefoot.com/';
							const citySlug = city.replace(/\s+/g, '-');
							const stateSlug = state.toUpperCase();
							const latitude =
								get(props, 'searchLocation.latitude') ||
								props?.facility?.location?.latitude;
							const longitude =
								get(props, 'searchLocation.longitude') ||
								props?.facility?.location?.longitude;
							const base = `https://www.sparefoot.com/${citySlug}-${stateSlug}-self-storage.html`;
							const locationParam = encodeURIComponent(
								`${city}, ${stateSlug}, USA`
							);
							if (!latitude || !longitude) {
								return `${base}?location=${locationParam}`;
							}

							return `${base}?location=${locationParam}&latitude=${latitude}&longitude=${longitude}`;
						})(),
						inLanguage: 'en-US',
						actionPlatform: [
							'http://schema.org/DesktopWebPlatform',
							'http://schema.org/MobileWebPlatform',
						],
					},
					'query-input': [
						{
							'@type': 'PropertyValueSpecification',
							valueRequired: 'http://schema.org/True',
							valueName: 'location',
						},
						{
							'@type': 'PropertyValueSpecification',
							valueRequired: 'http://schema.org/True',
							valueName: 'latitude',
						},
						{
							'@type': 'PropertyValueSpecification',
							valueRequired: 'http://schema.org/True',
							valueName: 'longitude',
						},
					],
				},
			},
			{
				'@type': 'WebPage',
				'@id': canonicalUrl,
				url: canonicalUrl,
				inLanguage: 'en',
				name: `${city} Self Storage`,
				isPartOf: { '@id': 'https://www.sparefoot.com' },
				description: `Browse self-storage facilities in ${city}, ${state}. Compare prices, amenities, and availability.`,
				mainEntity: (() => {
					if (!city || !state) {
						return undefined; // Avoid emitting incomplete Place
					}
					return {
						'@type': 'Place',
						name: `${city}, ${state}`,
						hasMap: mapUrl
							? {
									'@type': 'Map',
									name: `${city} Self Storage Map`,
									url: mapUrl,
							  }
							: undefined,
					};
				})(),
			},
		],
	};

	const offerCatalogSchema = {
		'@context': 'https://schema.org',
		'@type': 'OfferCatalog',
		name: 'Storage Units',
		itemListElement: {
			'@type': 'Offer',
			name: props?.facility?.name,
			priceSpecification: {
				'@type': 'PriceSpecification',
				price: props?.facility?.cheapestPrice?.value,
				priceCurrency: 'USD',
				valueAddedTaxIncluded: 'False',
			},
		},
	};
	const mapSchema = {
		'@context': 'https://schema.org',
		'@type': 'Map',
		name: 'Facility Map',
		url: `https://www.google.com/maps?q=${props?.facility?.location?.latitude},${props?.facility?.location?.longitude}`,
	};

	const clpMapSchema = {
		'@context': 'https://schema.org/',
		'@type': 'Map',
		name: 'Sparefoot',
		url: props.staticMapUrl,
	};

	const stringifiedPageSchema = JSON.stringify(pageSchema);
	const stringifiedPageSchemaV2 = JSON.stringify(pageSchemaV2);
	const stringifiedWebPageSchemaV2 = JSON.stringify(webPageSchemaV2);
	const stringifiedOfferCatalogSchema = JSON.stringify(offerCatalogSchema);
	const stringifiedMapSchema = JSON.stringify(mapSchema);
	const stringifiedClpMapSchema = JSON.stringify(clpMapSchema);
	const stringifiedClpPageSchema = JSON.stringify(clpPageSchema);

	const nearMeWebpageSchema = (() => {
		if (props.pageType !== 'storageNearMeLandingPage') {
			return null;
		}

		const pageName = props.primaryHeadline || 'Storage Near You';
		const pageUrl = props.match?.url
			? `https://www.sparefoot.com${props.match.url.split('?')[0]}`
			: props.canonicalWithQueryParams
			? props.canonicalWithQueryParams.split('?')[0]
			: props.canonicalUrl
			? props.canonicalUrl.split('?')[0]
			: '';
		const pageDescription =
			props.nmpMetaDescription ||
			props.metaDescription ||
			get(props, 'search.content.metaDescription', '');
		const primaryFacility = props.facilities?.[0];
		const itemListElement = primaryFacility
			? [
					{
						'@type': 'SelfStorage',
						name:
							get(primaryFacility, 'name') ||
							get(primaryFacility, 'facilityName') ||
							'',
						url: `${baseUrl}${get(primaryFacility, 'url.facility', '')}`,
					},
			  ]
			: [];

		return JSON.stringify({
			'@context': 'https://schema.org',
			'@type': 'WebPage',
			name: pageName,
			url: pageUrl,
			description: pageDescription,
			mainEntity: {
				'@type': 'ItemList',
				name: pageName,
				description: `List of storage facilities near the user's location`,
				itemListElement,
			},
		});
	})();

	const nearMeWebSiteSchema =
		props.pageType === 'storageNearMeLandingPage'
			? JSON.stringify({
					'@context': 'https://schema.org',
					'@type': 'WebSite',
					name: 'SpareFoot',
					url: 'https://www.sparefoot.com/',
					potentialAction: {
						'@type': 'SearchAction',
						target: {
							'@type': 'EntryPoint',
							urlTemplate: (() => {
								if (!city || !state)
									return 'https://www.sparefoot.com/';
								const stateSlug = state.toUpperCase();
								const latitude =
									get(props, 'searchLocation.latitude') ||
									props?.facility?.location?.latitude;
								const longitude =
									get(props, 'searchLocation.longitude') ||
									props?.facility?.location?.longitude;
								const base = `https://www.sparefoot.com/search`;
								const locationParam = encodeURIComponent(
									`${city}, ${stateSlug}, USA`
								);
								if (!latitude || !longitude) {
									return `${base}?location=${locationParam}`;
								}

								return `${base}?location=${locationParam}`;
							})(),
							inLanguage: 'en-US',
							actionPlatform: [
								'http://schema.org/DesktopWebPlatform',
								'http://schema.org/MobileWebPlatform',
							],
						},
						'query-input': [
							{
								'@type': 'PropertyValueSpecification',
								valueRequired: 'http://schema.org/True',
								valueName: 'location',
							},
						],
					},
			  })
			: null;

	// When parent provides pageSchema (e.g. SizeGuidePage), use it so we don't output a duplicate WebPage
	return {
		pageSchema: props.pageSchema ?? stringifiedPageSchema,
		pageSchemaV2: props.pageSchemaV2 ?? stringifiedPageSchemaV2,
		webPageSchemaV2: props.webPageSchemaV2 ?? stringifiedWebPageSchemaV2,
		offerCatalogSchema: stringifiedOfferCatalogSchema,
		mapSchema: stringifiedMapSchema,
		clpMapSchema: stringifiedClpMapSchema,
		clpPageSchema: stringifiedClpPageSchema,
		nearMeWebpageSchema: props.nearMeWebpageSchema ?? nearMeWebpageSchema,
		nearMeWebSiteSchema: props.nearMeWebSiteSchema ?? nearMeWebSiteSchema,
	};
});
